var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/// <reference path="../recharts.d.ts" />
import React, { useState, useEffect, useMemo, useCallback, useRef, } from "react";
import { Table, Button, Space, Popover, Checkbox, Divider, Tag, Switch, Badge, Select, Progress, } from "antd";
import { SettingOutlined, EditOutlined, DeleteOutlined, SettingOutlined as TestIcon, } from "@ant-design/icons";
import { Resizable } from "react-resizable";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, ReferenceLine, } from "recharts";
import "react-resizable/css/styles.css";
// 在组件顶部添加配置常量
var MIN_COLUMN_WIDTH = 10; // 最小列宽（设置为很小的值，允许列宽缩到很小）
var MAX_COLUMN_WIDTH = 600; // 最大列宽
var MiniChart = function (_a) {
    var data = _a.data, chartType = _a.chartType, xField = _a.xField, _b = _a.height, height = _b === void 0 ? 40 : _b, width = _a.width, _c = _a.color, color = _c === void 0 ? "#1890ff" : _c, _d = _a.smooth, smooth = _d === void 0 ? false : _d, referenceLines = _a.referenceLines;
    // 如果没有数据，返回空
    if (!data || data.length === 0) {
        return _jsx("div", { style: { height: height, width: width || "100%" } });
    }
    // 处理数据格式
    var processedData = [];
    var yFields = [];
    var seriesField;
    // 检查数据格式
    var firstItem = data[0];
    if (typeof firstItem === "number") {
        // 如果数据是数字数组，转换为对象格式
        processedData = data.map(function (item, index) {
            var _a;
            return (_a = {},
                _a[xField || "index"] = index,
                _a.value = typeof item === 'number' ? item : 0,
                _a);
        });
        yFields = ["value"];
    }
    else if (Array.isArray(firstItem)) {
        // 如果数据是二维数组，转换为多系列格式（长格式）
        var maxLength = Math.max.apply(Math, __spreadArray(__spreadArray([], data.map(function (arr) { return (Array.isArray(arr) ? arr.length : 0); }), false), [0], false));
        processedData = [];
        var _loop_1 = function (i) {
            data.forEach(function (arr, seriesIndex) {
                var _a;
                var _b;
                if (Array.isArray(arr)) {
                    processedData.push((_a = {},
                        _a[xField || "index"] = i,
                        _a.type = "series".concat(seriesIndex),
                        _a.value = (_b = arr[i]) !== null && _b !== void 0 ? _b : 0,
                        _a));
                }
            });
        };
        for (var i = 0; i < maxLength; i++) {
            _loop_1(i);
        }
        seriesField = "type";
        yFields = ["value"];
    }
    else if (typeof firstItem === "object") {
        // 如果数据已经是对象格式
        processedData = data;
        // 尝试自动检测 yField
        var keys_1 = Object.keys(firstItem);
        var xKey_1 = xField || "index";
        yFields = keys_1.filter(function (key) { return key !== xKey_1 && typeof firstItem[key] === "number"; });
        // 如果有多个数值字段，使用多系列
        if (yFields.length > 1) {
            seriesField = "type";
            // 转换为长格式数据
            var longData_1 = [];
            processedData.forEach(function (item) {
                yFields.forEach(function (field) {
                    var _a;
                    var _b;
                    longData_1.push((_a = {},
                        _a[xKey_1] = (_b = item[xKey_1]) !== null && _b !== void 0 ? _b : item[keys_1[0]],
                        _a.type = field,
                        _a.value = item[field],
                        _a));
                });
            });
            processedData = longData_1;
            yFields = ["value"];
        }
        else {
            yFields = yFields.length > 0 ? [yFields[0]] : ["value"];
        }
    }
    // 如果没有找到有效的数据字段，使用默认值
    if (yFields.length === 0) {
        yFields = ["value"];
    }
    // 确保最小高度为16px，但使用实际高度进行渲染
    var chartHeight = Math.max(height, 16);
    var chartWidth = width || "100%";
    // 处理颜色
    var colors = Array.isArray(color) ? color : [color];
    var primaryColor = colors[0] || "#1890ff";
    // 折线图配置
    if (chartType === "line") {
        // 如果是多系列数据
        if (seriesField) {
            // 按系列分组数据
            var seriesMap_1 = new Map();
            processedData.forEach(function (item) {
                var seriesType = item[seriesField];
                if (!seriesMap_1.has(seriesType)) {
                    seriesMap_1.set(seriesType, []);
                }
                seriesMap_1.get(seriesType).push(item);
            });
            // 转换为 recharts 需要的格式（按 x 值分组）
            var xValueMap_1 = new Map();
            processedData.forEach(function (item) {
                var _a;
                var xValue = item[xField || "index"];
                if (!xValueMap_1.has(xValue)) {
                    xValueMap_1.set(xValue, (_a = {}, _a[xField || "index"] = xValue, _a));
                }
                var seriesType = item[seriesField];
                xValueMap_1.get(xValue)[seriesType] = item[yFields[0] || "value"];
            });
            var chartData = Array.from(xValueMap_1.values());
            return (_jsx("div", __assign({ style: { height: chartHeight, width: chartWidth } }, { children: _jsx(ResponsiveContainer, __assign({ width: "100%", height: "100%" }, { children: _jsxs(LineChart, __assign({ data: chartData, margin: { top: 0, right: 0, bottom: 0, left: 0 } }, { children: [_jsx(XAxis, { dataKey: xField || "index", hide: true }), _jsx(YAxis, { hide: true }), _jsx(Tooltip, { content: function () { return null; } }), _jsx(Legend, { content: function () { return null; } }), Array.from(seriesMap_1.keys()).map(function (seriesType, index) { return (_jsx(Line, { type: smooth ? "monotone" : "linear", dataKey: seriesType, stroke: colors[index % colors.length], strokeWidth: chartHeight <= 16 ? 1.5 : 2, dot: false, isAnimationActive: false }, seriesType)); }), referenceLines === null || referenceLines === void 0 ? void 0 : referenceLines.map(function (refLine, index) { return (_jsx(ReferenceLine, { y: refLine.value, stroke: refLine.color || "#999", strokeDasharray: refLine.strokeDasharray || "5 5", label: refLine.label ? { value: refLine.label, position: "right" } : undefined }, "ref-line-".concat(refLine.value, "-").concat(index))); })] })) })) })));
        }
        // 单系列折线图
        return (_jsx("div", __assign({ style: { height: chartHeight, width: chartWidth } }, { children: _jsx(ResponsiveContainer, __assign({ width: "100%", height: "100%" }, { children: _jsxs(LineChart, __assign({ data: processedData, margin: { top: 0, right: 0, bottom: 0, left: 0 } }, { children: [_jsx(XAxis, { dataKey: xField || "index", hide: true }), _jsx(YAxis, { hide: true }), _jsx(Tooltip, { content: function () { return null; } }), _jsx(Line, { type: smooth ? "monotone" : "linear", dataKey: yFields[0] || "value", stroke: primaryColor, strokeWidth: chartHeight <= 16 ? 1.5 : 2, dot: false, isAnimationActive: false }), referenceLines === null || referenceLines === void 0 ? void 0 : referenceLines.map(function (refLine, index) { return (_jsx(ReferenceLine, { y: refLine.value, stroke: refLine.color || "#999", strokeDasharray: refLine.strokeDasharray || "5 5", label: refLine.label ? { value: refLine.label, position: "right" } : undefined }, String(index))); })] })) })) })));
    }
    // 柱状图配置
    if (chartType === "column") {
        // 如果是多系列数据
        if (seriesField) {
            // 按系列分组数据
            var seriesMap_2 = new Map();
            processedData.forEach(function (item) {
                var seriesType = item[seriesField];
                if (!seriesMap_2.has(seriesType)) {
                    seriesMap_2.set(seriesType, []);
                }
                seriesMap_2.get(seriesType).push(item);
            });
            // 转换为 recharts 需要的格式（按 x 值分组）
            var xValueMap_2 = new Map();
            processedData.forEach(function (item) {
                var _a;
                var xValue = item[xField || "index"];
                if (!xValueMap_2.has(xValue)) {
                    xValueMap_2.set(xValue, (_a = {}, _a[xField || "index"] = xValue, _a));
                }
                var seriesType = item[seriesField];
                xValueMap_2.get(xValue)[seriesType] = item[yFields[0] || "value"];
            });
            var chartData = Array.from(xValueMap_2.values());
            return (_jsx("div", __assign({ style: { height: chartHeight, width: chartWidth } }, { children: _jsx(ResponsiveContainer, __assign({ width: "100%", height: "100%" }, { children: _jsxs(BarChart, __assign({ data: chartData, margin: { top: 0, right: 0, bottom: 0, left: 0 } }, { children: [_jsx(XAxis, { dataKey: xField || "index", hide: true }), _jsx(YAxis, { hide: true }), _jsx(Tooltip, { content: function () { return null; } }), _jsx(Legend, { content: function () { return null; } }), Array.from(seriesMap_2.keys()).map(function (seriesType, index) { return (_jsx(Bar, __assign({ dataKey: seriesType, fill: colors[index % colors.length] }, { radius: [2, 2, 0, 0] }, { isAnimationActive: false }), seriesType)); }), referenceLines === null || referenceLines === void 0 ? void 0 : referenceLines.map(function (refLine, index) { return (_jsx(ReferenceLine, { y: refLine.value, stroke: refLine.color || "#999", strokeDasharray: refLine.strokeDasharray || "5 5", label: refLine.label ? { value: refLine.label, position: "right" } : undefined }, "ref-line-".concat(refLine.value, "-").concat(index))); })] })) })) })));
        }
        // 单系列柱状图
        return (_jsx("div", __assign({ style: { height: chartHeight, width: chartWidth } }, { children: _jsx(ResponsiveContainer, __assign({ width: "100%", height: "100%" }, { children: _jsxs(BarChart, __assign({ data: processedData, margin: { top: 0, right: 0, bottom: 0, left: 0 } }, { children: [_jsx(XAxis, { dataKey: xField || "index", hide: true }), _jsx(YAxis, { hide: true }), _jsx(Tooltip, { content: function () { return null; } }), _jsx(Bar, __assign({ dataKey: yFields[0] || "value", fill: primaryColor }, { radius: [2, 2, 0, 0] }, { isAnimationActive: false })), referenceLines === null || referenceLines === void 0 ? void 0 : referenceLines.map(function (refLine, index) { return (_jsx(ReferenceLine, { y: refLine.value, stroke: refLine.color || "#999", strokeDasharray: refLine.strokeDasharray || "5 5", label: refLine.label ? { value: refLine.label, position: "right" } : undefined }, String(index))); })] })) })) })));
    }
    return null;
};
// 对齐选择器组件
var AlignSelectCell = function (_a) {
    var value = _a.value, record = _a.record, dataIndex = _a.dataIndex, onAlignChange = _a.onAlignChange;
    var _b = useState(false), editing = _b[0], setEditing = _b[1];
    var alignValue = value || "left";
    var alignLabel = alignValue === "left" ? "左" : alignValue === "center" ? "中" : "右";
    var options = [
        { label: "左", value: "left" },
        { label: "中", value: "center" },
        { label: "右", value: "right" },
    ];
    if (editing) {
        return (_jsx(Select, { value: alignValue, open: true, autoFocus: true, onBlur: function () { return setEditing(false); }, onChange: function (newAlign) {
                if (onAlignChange) {
                    onAlignChange(newAlign, record, dataIndex || "");
                }
                setEditing(false);
            }, size: "small", style: { width: "100%" }, options: options }));
    }
    return (_jsx("div", __assign({ onClick: function () { return setEditing(true); }, style: {
            cursor: "pointer",
            padding: "4px 8px",
            minHeight: "22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        } }, { children: alignLabel })));
};
// 生成单元格组件渲染函数
var createCellRenderer = function (cellType, cellConfig, dataIndex) {
    switch (cellType) {
        case "tag": {
            return function (value) {
                if (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.options) {
                    var option = cellConfig.options.find(function (opt) { return opt.value === value; });
                    if (option) {
                        return (_jsx(Tag, __assign({ color: option.color || cellConfig.color }, { children: option.label })));
                    }
                }
                return _jsx(Tag, __assign({ color: cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.color }, { children: value }));
            };
        }
        case "switch": {
            return function (value, record, index) {
                var _a;
                var disabled = typeof (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.disabled) === "function"
                    ? cellConfig.disabled(record)
                    : ((_a = cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.disabled) !== null && _a !== void 0 ? _a : false);
                return (_jsx(Switch, { size: (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.size) || "default", checked: value, disabled: disabled, onChange: function (checked) {
                        if (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.onChange) {
                            cellConfig.onChange(checked, record, dataIndex || "");
                        }
                    } }));
            };
        }
        case "badge": {
            return function (value) {
                return (_jsx(Badge, { status: (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.status) || "default", text: (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.text) || value }));
            };
        }
        case "alignSelect": {
            return function (value, record, index) {
                return (_jsx(AlignSelectCell, { value: value, record: record, dataIndex: dataIndex, onAlignChange: cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.onAlignChange }));
            };
        }
        case "minichart": {
            return function (value, record, index) {
                // 获取图表数据
                var chartData = [];
                // 如果 value 是数组，直接使用
                if (Array.isArray(value)) {
                    chartData = value;
                    // 确保数组中有有效的数字
                    if (chartData.length > 0 && typeof chartData[0] === 'number') {
                        // 数字数组格式正确
                    }
                    else {
                        // 如果不是数字数组，尝试转换
                        chartData = chartData.filter(function (item) { return typeof item === 'number'; });
                    }
                }
                // 如果配置了 dataField，从 record 中获取数据
                else if (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.dataField) {
                    var dataField = cellConfig.dataField;
                    if (Array.isArray(dataField)) {
                        // 多系列数据：将多个字段的数据组合成二维数组
                        var seriesData = dataField.map(function (field) {
                            var fieldData = record[field];
                            return Array.isArray(fieldData) ? fieldData : [];
                        });
                        // 如果所有系列都是数组，则传递二维数组给 MiniChart
                        if (seriesData.every(function (arr) { return arr.length > 0; })) {
                            chartData = seriesData;
                        }
                    }
                    else {
                        // 单系列数据
                        var fieldData = record[dataField];
                        chartData = Array.isArray(fieldData) ? fieldData : [];
                    }
                }
                // 如果 value 是对象且有数据字段
                else if (value && typeof value === "object" && !Array.isArray(value)) {
                    // 尝试从对象中提取数组数据
                    var keys = Object.keys(value);
                    var arrayKeys = keys.filter(function (key) { return Array.isArray(value[key]); });
                    if (arrayKeys.length > 0) {
                        // 如果有多个数组字段，组合成多系列
                        if (arrayKeys.length > 1) {
                            chartData = arrayKeys.map(function (key) { return value[key]; });
                        }
                        else {
                            chartData = value[arrayKeys[0]];
                        }
                    }
                }
                // 如果没有数据，返回空
                if (!chartData || chartData.length === 0) {
                    return _jsx("div", { style: { height: (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.height) || 40, width: '100%', backgroundColor: '#f0f0f0' } });
                }
                // 确保数据有效：支持一维 number[] 或二维 number[][]（多系列）
                var validData = [];
                if (Array.isArray(chartData)) {
                    var first = chartData[0];
                    if (typeof first === 'number') {
                        validData = chartData.filter(function (item) { return typeof item === 'number'; });
                    }
                    else if (Array.isArray(first)) {
                        validData = chartData.map(function (arr) {
                            return Array.isArray(arr) ? arr.filter(function (item) { return typeof item === 'number'; }) : [];
                        }).filter(function (arr) { return arr.length > 0; });
                    }
                    else {
                        validData = chartData.filter(function (item) { return typeof item === 'number'; });
                    }
                }
                var hasData = Array.isArray(validData) && (validData.length > 0 &&
                    (typeof validData[0] === 'number' || (Array.isArray(validData[0]) && validData[0].length > 0)));
                if (!hasData) {
                    return _jsx("div", __assign({ style: { height: (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.height) || 40, width: '100%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#999' } }, { children: "\u65E0\u6570\u636E" }));
                }
                // 确保高度至少为16px
                var chartHeight = Math.max((cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.height) || 40, 16);
                return (_jsx("div", __assign({ style: {
                        width: '100%',
                        height: chartHeight,
                        minHeight: chartHeight,
                        position: 'relative',
                        overflow: 'visible',
                        display: 'block',
                    } }, { children: _jsx(MiniChart, { data: validData, chartType: (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.chartType) || "line", xField: cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.xField, height: chartHeight, width: undefined, color: cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.chartColor, smooth: cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.smooth, referenceLines: 
                        // 优先使用 cellConfig 中的 referenceLines
                        // 如果没有，尝试从 record 中读取 normalizedReferenceLines
                        (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.referenceLines) ||
                            (record === null || record === void 0 ? void 0 : record.normalizedReferenceLines) }) })));
            };
        }
        case "progress": {
            return function (value, record, index) {
                // 获取进度百分比
                var percent = (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.percent) !== undefined
                    ? cellConfig.percent
                    : (typeof value === "number" ? value : Number(value) || 0);
                // 限制在 0-100 之间
                var clampedPercent = Math.min(Math.max(percent, 0), 100);
                return (_jsx(Progress, { percent: clampedPercent, strokeColor: (cellConfig === null || cellConfig === void 0 ? void 0 : cellConfig.strokeColor) || "#1890ff", trailColor: "#f0f0f0", showInfo: false, size: "small", style: { width: "100%" } }));
            };
        }
        case "text":
        default: {
            return function (value) { return value; };
        }
    }
};
// 添加ResizableTitle组件
var ResizableTitle = function (props) {
    var onResize = props.onResize, width = props.width, _a = props.cellComponent, CellComponent = _a === void 0 ? "th" : _a, restProps = __rest(props, ["onResize", "width", "cellComponent"]);
    var HeaderCellComponent = CellComponent;
    if (!width) {
        return _jsx(HeaderCellComponent, __assign({}, restProps));
    }
    return (_jsx(Resizable, __assign({ width: width, height: 0, onResize: onResize, draggableOpts: { enableUserSelectHack: false }, 
        // 添加最小最大宽度限制（最小宽度设为10，允许列宽缩到很小）
        minConstraints: [10, 0], maxConstraints: [MAX_COLUMN_WIDTH, 0], handle: _jsx("span", { className: "react-resizable-handle", style: {
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: "col-resize",
                zIndex: 1000,
                userSelect: "none",
                touchAction: "none",
            } }) }, { children: _jsx(HeaderCellComponent, __assign({}, restProps, { style: __assign(__assign({}, restProps.style), { position: "relative", userSelect: "none" }) })) })));
};
/**
 * 通用表格组件（内核）
 * 只包含表格核心功能，不包含容器
 */
function CompsTable(_a) {
    var _b, _c, _d;
    var tableProps = _a.tableProps, _e = _a.loading, loading = _e === void 0 ? false : _e, _f = _a.showSelection, showSelection = _f === void 0 ? false : _f, pagination = _a.pagination, onPaginationChange = _a.onPaginationChange, _g = _a.defaultPageSize, defaultPageSize = _g === void 0 ? 10 : _g, _h = _a.showColumnSetting, showColumnSetting = _h === void 0 ? true : _h, storageKey = _a.storageKey, _j = _a.resizableColumns, resizableColumns = _j === void 0 ? false : _j, _k = _a.saveColumnWidths, saveColumnWidths = _k === void 0 ? true : _k, onColumnWidthChange = _a.onColumnWidthChange, defaultActions = _a.defaultActions, summary = _a.summary, summaryWithColumns = _a.summaryWithColumns, footer = _a.footer, className = _a.className, style = _a.style, onColumnSettingButtonRender = _a.onColumnSettingButtonRender, onSelectionChange = _a.onSelectionChange, _l = _a.autoScroll, autoScroll = _l === void 0 ? true : _l, _m = _a.scrollOffset, scrollOffset = _m === void 0 ? 0 : _m;
    var _o = useState([]), selectedRowKeys = _o[0], setSelectedRowKeys = _o[1];
    // selectedRows 状态用于传递给 onSelectionChange 回调
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _p = useState([]), selectedRows = _p[0], setSelectedRows = _p[1];
    var _q = useState(false), popoverVisible = _q[0], setPopoverVisible = _q[1];
    // 表格容器引用和滚动高度
    var containerRef = useRef(null);
    var _r = useState(undefined), scrollHeight = _r[0], setScrollHeight = _r[1];
    // 内部分页状态（当没有传入分页时使用）
    var _s = useState({
        current: 1,
        pageSize: defaultPageSize,
        total: 0,
    }), internalPagination = _s[0], setInternalPagination = _s[1];
    // 使用外部传入的分页或内部分页
    // 如果 tableProps.pagination 明确设置为 false，则不显示分页
    var currentPagination = tableProps.pagination === false ? false : pagination || internalPagination;
    // 计算滚动高度
    useEffect(function () {
        if (!autoScroll || !containerRef.current) {
            return;
        }
        var calculateScrollHeight = function () {
            var container = containerRef.current;
            if (!container)
                return;
            // 获取容器总高度
            var containerHeight = container.clientHeight;
            if (containerHeight === 0) {
                // 容器还没有高度，设置为 undefined，等待下次计算
                setScrollHeight(undefined);
                return;
            }
            // 获取表头高度（表头是固定的，需要从滚动区域中减去）
            var thead = container.querySelector(".ant-table-thead");
            var theadHeight = thead ? thead.getBoundingClientRect().height : 0;
            // 获取分页高度（分页在表格外部，固定在底部）
            // 如果分页存在，使用实际高度；如果不存在，使用默认高度预留空间
            var pagination = container.querySelector(".ant-pagination");
            var paginationHeight = 0;
            // 如果分页被禁用，则不需要预留空间
            // 注意：这里需要重新获取 currentPagination，因为闭包可能捕获旧值
            var actualPagination = tableProps.pagination === false
                ? false
                : pagination || internalPagination;
            if (actualPagination !== false) {
                if (pagination) {
                    var paginationRect = pagination.getBoundingClientRect();
                    paginationHeight = paginationRect.height;
                    // 获取分页的 margin
                    var paginationStyle = window.getComputedStyle(pagination);
                    var marginTop = parseFloat(paginationStyle.marginTop) || 0;
                    var marginBottom = parseFloat(paginationStyle.marginBottom) || 0;
                    paginationHeight += marginTop + marginBottom;
                }
                else {
                    // 分页还没渲染，使用默认高度预留空间（通常分页高度约 64px，包括 margin）
                    paginationHeight = 64;
                }
            }
            // 计算表格 body 的滚动高度
            // scroll.y 是表格 body 的高度，不包括表头
            // 所以：容器高度 - 表头高度 - 分页高度 - 偏移量 = 表格 body 高度
            var bodyHeight = containerHeight - theadHeight - paginationHeight - scrollOffset;
            // 设置最小高度为 100px，确保表格可以显示
            // 但如果容器本身很小，也要允许表格缩小
            var finalHeight = Math.max(bodyHeight, 100);
            setScrollHeight(finalHeight);
        };
        // 初始计算（延迟执行，确保 DOM 渲染完成）
        // 多次尝试计算，因为 DOM 可能需要时间渲染
        var initialTimer1 = setTimeout(calculateScrollHeight, 50);
        var initialTimer2 = setTimeout(calculateScrollHeight, 150);
        var initialTimer3 = setTimeout(calculateScrollHeight, 300);
        // 使用 ResizeObserver 监听容器大小变化
        var resizeObserver = new ResizeObserver(function (entries) {
            // 延迟执行，确保 DOM 更新完成
            // 使用 requestAnimationFrame 确保在下一帧计算
            requestAnimationFrame(function () {
                setTimeout(calculateScrollHeight, 0);
            });
        });
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        // 监听窗口大小变化
        window.addEventListener("resize", calculateScrollHeight);
        return function () {
            clearTimeout(initialTimer1);
            clearTimeout(initialTimer2);
            clearTimeout(initialTimer3);
            resizeObserver.disconnect();
            window.removeEventListener("resize", calculateScrollHeight);
        };
    }, [
        autoScroll,
        scrollOffset,
        loading,
        pagination,
        tableProps.pagination,
        internalPagination,
        // 分页显隐、条数变化时重算 body 高度，避免分页被挤出可视区
        currentPagination === false
            ? "off"
            : "".concat((_b = currentPagination === null || currentPagination === void 0 ? void 0 : currentPagination.current) !== null && _b !== void 0 ? _b : "", "-").concat((_c = currentPagination === null || currentPagination === void 0 ? void 0 : currentPagination.pageSize) !== null && _c !== void 0 ? _c : "", "-").concat((_d = currentPagination === null || currentPagination === void 0 ? void 0 : currentPagination.total) !== null && _d !== void 0 ? _d : ""),
    ]);
    // 添加列宽状态
    var _t = useState({}), columnWidths = _t[0], setColumnWidths = _t[1];
    // 列可见性状态
    var _u = useState({}), columnVisibility = _u[0], setColumnVisibility = _u[1];
    // 获取表格所有列
    var allColumns = useMemo(function () { return tableProps.columns || []; }, [tableProps.columns]);
    var rowKeyProp = tableProps.rowKey;
    var resolveRowKey = useCallback(function (record, index) {
        var _a;
        if (typeof rowKeyProp === "function") {
            return rowKeyProp(record, index);
        }
        if (typeof rowKeyProp === "string") {
            return record[rowKeyProp];
        }
        return (_a = record === null || record === void 0 ? void 0 : record.key) !== null && _a !== void 0 ? _a : index;
    }, [rowKeyProp]);
    // 存储键
    var actualStorageKey = useMemo(function () { return storageKey || "table_columns_default"; }, [storageKey]);
    // 为每列生成唯一标识
    var getColumnKey = useCallback(function (column) {
        var _a;
        return ((_a = (column.dataIndex || column.key || column.title)) === null || _a === void 0 ? void 0 : _a.toString()) || "";
    }, []);
    // 处理单元格组件的辅助函数
    var processCellComponent = useCallback(function (col) {
        // 扩展列类型，支持单元格组件
        var extendedCol = col;
        // 如果指定了 cellType 且没有自定义 render，自动生成 render 函数
        if (extendedCol.cellType && !col.render) {
            var dataIndex = void 0;
            if (typeof col.dataIndex === "string") {
                dataIndex = col.dataIndex;
            }
            else if (Array.isArray(col.dataIndex)) {
                // 将数组中的每个元素转换为字符串
                dataIndex = col.dataIndex.map(String);
            }
            else if (col.dataIndex !== undefined && col.dataIndex !== null) {
                dataIndex = String(col.dataIndex);
            }
            else {
                dataIndex = undefined;
            }
            var finalRender = createCellRenderer(extendedCol.cellType, extendedCol.cellConfig, dataIndex);
            return __assign(__assign({}, col), { render: finalRender });
        }
        return col;
    }, []);
    // 过滤显示的列 - 只定义一次，并处理单元格组件
    var visibleColumns = useMemo(function () {
        return allColumns
            .filter(function (column) {
            var key = getColumnKey(column);
            return !key || columnVisibility[key] !== false;
        })
            .map(processCellComponent);
    }, [allColumns, columnVisibility, getColumnKey, processCellComponent]);
    // 初始化列宽
    useEffect(function () {
        if (resizableColumns && saveColumnWidths) {
            try {
                var savedWidths = localStorage.getItem("".concat(actualStorageKey, "_widths"));
                if (savedWidths) {
                    setColumnWidths(JSON.parse(savedWidths));
                }
            }
            catch (error) {
                console.error("加载列宽配置失败:", error);
            }
        }
    }, [resizableColumns, saveColumnWidths, actualStorageKey]);
    // 保存列宽到本地存储
    useEffect(function () {
        if (resizableColumns &&
            saveColumnWidths &&
            Object.keys(columnWidths).length > 0) {
            try {
                localStorage.setItem("".concat(actualStorageKey, "_widths"), JSON.stringify(columnWidths));
            }
            catch (error) {
                console.error("保存列宽配置失败:", error);
            }
        }
    }, [columnWidths, resizableColumns, saveColumnWidths, actualStorageKey]);
    // 处理列宽调整
    var handleResize = useCallback(function (index) {
        return function (e, _a) {
            var size = _a.size;
            var column = visibleColumns[index];
            var key = column ? getColumnKey(column) : null;
            if (!key) {
                return;
            }
            var newWidth = size.width;
            // 最小宽度设为10，允许列宽缩到很小
            if (newWidth < 10)
                newWidth = 10;
            if (newWidth > MAX_COLUMN_WIDTH)
                newWidth = MAX_COLUMN_WIDTH;
            setColumnWidths(function (prev) {
                var _a;
                if (prev[key] === newWidth) {
                    return prev;
                }
                var newWidths = __assign(__assign({}, prev), (_a = {}, _a[key] = newWidth, _a));
                // 调用外部回调通知列宽变化
                if (onColumnWidthChange) {
                    onColumnWidthChange(key, newWidth);
                }
                return newWidths;
            });
        };
    }, [visibleColumns, getColumnKey, onColumnWidthChange]);
    var userComponents = tableProps.components;
    var userRowSelection = tableProps.rowSelection;
    // 处理表格组件
    var mergedComponents = useMemo(function () {
        if (!resizableColumns) {
            return userComponents;
        }
        var headerComponents = userComponents === null || userComponents === void 0 ? void 0 : userComponents.header;
        var headerCellComponent = (headerComponents === null || headerComponents === void 0 ? void 0 : headerComponents.cell) && typeof headerComponents.cell !== "undefined"
            ? headerComponents.cell
            : "th";
        var ResizableHeaderCell = function (props) { return (_jsx(ResizableTitle, __assign({}, props, { cellComponent: headerCellComponent }))); };
        return __assign(__assign({}, userComponents), { header: __assign(__assign({}, (headerComponents || {})), { cell: ResizableHeaderCell }) });
    }, [resizableColumns, userComponents]);
    // 添加列宽重置函数
    var resetColumnWidths = useCallback(function () {
        setColumnWidths({});
        try {
            localStorage.removeItem("".concat(actualStorageKey, "_widths"));
        }
        catch (error) {
            console.error("移除列宽配置失败:", error);
        }
        setPopoverVisible(false);
    }, [actualStorageKey]);
    // 设置列宽和处理函数
    // 注意：visibleColumns 已经处理了 cellType，所以这里只需要处理列宽
    var resizedColumns = useMemo(function () {
        return visibleColumns.map(function (col, index) {
            var key = getColumnKey(col);
            var savedWidth = key && columnWidths[key] ? columnWidths[key] : undefined;
            // 确保所有列都有 width，如果没有则使用默认值或最小宽度（降低到10）
            var width = savedWidth || col.width || 10;
            var originalOnHeaderCell = col.onHeaderCell;
            var resizeHandler = handleResize(index);
            return __assign(__assign({}, col), { width: width, onHeaderCell: function (column) {
                    var originalProps = typeof originalOnHeaderCell === "function"
                        ? originalOnHeaderCell(column) || {}
                        : {};
                    var originalResize = originalProps.onResize;
                    return __assign(__assign({}, originalProps), { width: width, onResize: function (event, data) {
                            if (typeof originalResize === "function") {
                                originalResize(event, data);
                            }
                            resizeHandler(event, data);
                        } });
                } });
        });
    }, [visibleColumns, columnWidths, getColumnKey, handleResize]);
    var visibilityInitializedRef = useRef(null);
    var getDefaultVisibility = useCallback(function () {
        var defaultVisibility = {};
        allColumns.forEach(function (column) {
            var key = getColumnKey(column);
            if (key) {
                defaultVisibility[key] = true;
            }
        });
        return defaultVisibility;
    }, [allColumns, getColumnKey]);
    // 初始化列可见性并在列变更时补全/清理配置
    useEffect(function () {
        if (typeof window === "undefined") {
            return;
        }
        // 初次或 storageKey 变更时加载本地配置
        if (visibilityInitializedRef.current !== actualStorageKey) {
            visibilityInitializedRef.current = actualStorageKey;
            try {
                var savedConfig = localStorage.getItem(actualStorageKey);
                if (savedConfig) {
                    var parsedConfig_1 = JSON.parse(savedConfig);
                    setColumnVisibility(function (prev) {
                        var mergedConfig = __assign(__assign({}, getDefaultVisibility()), parsedConfig_1);
                        var isSame = Object.keys(mergedConfig).length === Object.keys(prev).length &&
                            Object.keys(mergedConfig).every(function (key) { return prev[key] === mergedConfig[key]; });
                        return isSame ? prev : mergedConfig;
                    });
                }
                else {
                    setColumnVisibility(function (prev) {
                        var defaultVisibility = getDefaultVisibility();
                        var isSame = Object.keys(defaultVisibility).length ===
                            Object.keys(prev).length &&
                            Object.keys(defaultVisibility).every(function (key) { return prev[key] === defaultVisibility[key]; });
                        return isSame ? prev : defaultVisibility;
                    });
                }
            }
            catch (error) {
                console.error("加载列配置失败:", error);
                setColumnVisibility(function (prev) {
                    var fallbackVisibility = getDefaultVisibility();
                    var isSame = Object.keys(fallbackVisibility).length ===
                        Object.keys(prev).length &&
                        Object.keys(fallbackVisibility).every(function (key) { return prev[key] === fallbackVisibility[key]; });
                    return isSame ? prev : fallbackVisibility;
                });
            }
            return;
        }
        // 当列集合变化时补充/清理可见性配置
        setColumnVisibility(function (prev) {
            var nextVisibility = __assign({}, prev);
            var changed = false;
            // 补充新列
            allColumns.forEach(function (column) {
                var key = getColumnKey(column);
                if (!key) {
                    return;
                }
                if (!(key in nextVisibility)) {
                    nextVisibility[key] = true;
                    changed = true;
                }
            });
            // 移除已不存在的列
            Object.keys(nextVisibility).forEach(function (key) {
                var stillExists = allColumns.some(function (column) { return getColumnKey(column) === key; });
                if (!stillExists) {
                    delete nextVisibility[key];
                    changed = true;
                }
            });
            return changed ? nextVisibility : prev;
        });
    }, [actualStorageKey, allColumns, getColumnKey, getDefaultVisibility]);
    // 保存列可见性到本地存储
    useEffect(function () {
        // 仅当columnVisibility不为空对象时保存
        if (Object.keys(columnVisibility).length > 0) {
            try {
                localStorage.setItem(actualStorageKey, JSON.stringify(columnVisibility));
            }
            catch (error) {
                console.error("保存列配置失败:", error);
            }
        }
    }, [columnVisibility, actualStorageKey]);
    // 处理列可见性变化
    var handleColumnVisibilityChange = useCallback(function (checkedValues) {
        var newVisibility = {};
        // 先将所有列设为不可见
        allColumns.forEach(function (column) {
            var key = getColumnKey(column);
            if (key) {
                newVisibility[key] = false;
            }
        });
        // 再将选中的列设为可见
        checkedValues.forEach(function (value) {
            newVisibility[value] = true;
        });
        setColumnVisibility(newVisibility);
    }, [allColumns, getColumnKey]);
    // 重置列可见性，恢复所有列显示
    var resetColumnVisibility = useCallback(function () {
        var resetVisibility = {};
        allColumns.forEach(function (column) {
            var key = getColumnKey(column);
            if (key) {
                resetVisibility[key] = true;
            }
        });
        setColumnVisibility(resetVisibility);
        setPopoverVisible(false);
    }, [allColumns, getColumnKey]);
    // 处理表格选择变化
    var handleSelectionChange = useCallback(function (newSelectedRowKeys, newSelectedRows) {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
        // 通知外部组件选中行变化
        if (onSelectionChange) {
            onSelectionChange(newSelectedRows);
        }
    }, [onSelectionChange]);
    // 处理表格变化
    var handleTableChange = function (newPagination, filters, sorter, extra) {
        // 如果有外部传入的回调，则调用
        if (onPaginationChange) {
            onPaginationChange(newPagination);
        }
        else {
            // 否则使用内部状态
            setInternalPagination(newPagination);
        }
        // 确保传递所有参数
        if (tableProps.onChange) {
            tableProps.onChange(newPagination, filters, sorter, extra);
        }
    };
    // 列设置弹窗内容
    var columnSettingContent = useMemo(function () {
        if (!showColumnSetting) {
            return null;
        }
        return (_jsxs("div", __assign({ style: { maxWidth: 300 } }, { children: [_jsxs("div", __assign({ style: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                    } }, { children: [_jsx("span", __assign({ style: { fontWeight: "bold" } }, { children: "\u663E\u793A\u7684\u5217" })), _jsxs(Space, { children: [_jsx(Button, __assign({ type: "link", size: "small", onClick: resetColumnVisibility, title: "\u91CD\u7F6E\u5217\u663E\u793A", style: { fontSize: "12px" } }, { children: "\u91CD\u7F6E\u663E\u793A" })), _jsx(Button, __assign({ type: "link", size: "small", onClick: resetColumnWidths, title: "\u91CD\u7F6E\u5217\u5BBD", danger: true, style: { fontSize: "12px" } }, { children: "\u91CD\u7F6E\u5BBD\u5EA6" }))] })] })), _jsx(Divider, { style: { margin: "8px 0" } }), _jsx(Checkbox.Group, __assign({ style: { display: "flex", flexDirection: "column", gap: "8px" }, value: Object.entries(columnVisibility)
                        .filter(function (_a) {
                        var _ = _a[0], visible = _a[1];
                        return visible;
                    })
                        .map(function (_a) {
                        var key = _a[0];
                        return key;
                    }), onChange: handleColumnVisibilityChange }, { children: allColumns.map(function (column) {
                        var key = getColumnKey(column);
                        if (!key)
                            return null;
                        var title = typeof column.title === "string" ? column.title : "\u5217 ".concat(key);
                        return (_jsx(Checkbox, __assign({ value: key }, { children: title }), key));
                    }) }))] })));
    }, [
        allColumns,
        columnVisibility,
        getColumnKey,
        handleColumnVisibilityChange,
        resetColumnVisibility,
        resetColumnWidths,
        showColumnSetting,
    ]);
    // 列设置按钮
    var columnSettingButton = useMemo(function () {
        if (!showColumnSetting || !columnSettingContent) {
            return null;
        }
        return (_jsx(Tooltip, __assign({ title: "\u5217\u8BBE\u7F6E" }, { children: _jsx(Popover, __assign({ content: columnSettingContent, title: null, trigger: "click", open: popoverVisible, onOpenChange: setPopoverVisible, placement: "rightTop", overlayStyle: { minWidth: "60px" } }, { children: _jsx(Button, { icon: React.createElement(SettingOutlined), type: "text", size: "small", style: { color: "#666" }, onClick: function (e) {
                        e.stopPropagation();
                        setPopoverVisible(!popoverVisible);
                    } }) })) })));
    }, [columnSettingContent, popoverVisible, showColumnSetting]);
    var columnSettingButtonSignature = useMemo(function () {
        var columnKeys = allColumns
            .map(function (column) { return getColumnKey(column); })
            .join("|");
        var visibleKeys = Object.entries(columnVisibility)
            .filter(function (_a) {
            var visible = _a[1];
            return visible;
        })
            .map(function (_a) {
            var key = _a[0];
            return key;
        })
            .sort()
            .join("|");
        return "".concat(columnKeys, "__").concat(visibleKeys, "__").concat(popoverVisible ? "open" : "closed");
    }, [allColumns, columnVisibility, getColumnKey, popoverVisible]);
    var lastRenderedSignatureRef = useRef(null);
    useEffect(function () {
        lastRenderedSignatureRef.current = null;
    }, [onColumnSettingButtonRender]);
    // 将列设置按钮传递给父组件
    useEffect(function () {
        if (!onColumnSettingButtonRender) {
            return;
        }
        if (columnSettingButtonSignature === lastRenderedSignatureRef.current) {
            return;
        }
        lastRenderedSignatureRef.current = columnSettingButtonSignature;
        onColumnSettingButtonRender(columnSettingButton);
    }, [
        columnSettingButton,
        columnSettingButtonSignature,
        onColumnSettingButtonRender,
    ]);
    // 创建缺省操作列
    var createDefaultActionColumn = function () {
        if (!defaultActions)
            return null;
        var onEdit = defaultActions.onEdit, onDelete = defaultActions.onDelete, onTest = defaultActions.onTest, editIcon = defaultActions.editIcon, deleteIcon = defaultActions.deleteIcon, testIcon = defaultActions.testIcon, _a = defaultActions.width, width = _a === void 0 ? 120 : _a, _b = defaultActions.spacing, spacing = _b === void 0 ? 16 : _b;
        // 如果没有提供任何操作回调，则不创建操作列
        if (!onEdit && !onDelete && !onTest)
            return null;
        return {
            title: "操作",
            key: "defaultActions",
            width: width,
            fixed: "right",
            render: function (_, record) {
                var actions = [];
                if (onEdit) {
                    actions.push(_jsx(Button, { type: "link", size: "small", icon: editIcon || React.createElement(EditOutlined), onClick: function () { return onEdit(record); }, style: { padding: 0, fontSize: "12px" } }, "edit"));
                }
                if (onTest) {
                    actions.push(_jsx(Button, { type: "link", size: "small", icon: testIcon || React.createElement(TestIcon), onClick: function () { return onTest(record); }, style: { padding: 0, fontSize: "12px" } }, "test"));
                }
                if (onDelete) {
                    actions.push(_jsx(Button, { type: "link", size: "small", danger: true, icon: deleteIcon || React.createElement(DeleteOutlined), onClick: function () { return onDelete(record); }, style: { padding: 0, fontSize: "12px" } }, "delete"));
                }
                return _jsx(Space, __assign({ size: spacing }, { children: actions }));
            },
        };
    };
    // 获取缺省操作列
    var defaultActionColumn = useMemo(function () { return createDefaultActionColumn(); }, [defaultActions]);
    var mergedRowSelection = useMemo(function () {
        if (!showSelection) {
            return userRowSelection;
        }
        var userOnChange = userRowSelection === null || userRowSelection === void 0 ? void 0 : userRowSelection.onChange;
        return __assign(__assign({}, (userRowSelection || {})), { selectedRowKeys: selectedRowKeys, onChange: function (keys, rows, info) {
                handleSelectionChange(keys, rows);
                if (typeof userOnChange === "function") {
                    userOnChange(keys, rows, info);
                }
            } });
    }, [showSelection, userRowSelection, selectedRowKeys, handleSelectionChange]);
    useEffect(function () {
        if (!showSelection) {
            return;
        }
        var externalSelectedKeys = userRowSelection === null || userRowSelection === void 0 ? void 0 : userRowSelection.selectedRowKeys;
        if (!Array.isArray(externalSelectedKeys)) {
            return;
        }
        setSelectedRowKeys(function (prevKeys) {
            if (Array.isArray(prevKeys) &&
                prevKeys.length === externalSelectedKeys.length &&
                prevKeys.every(function (key, index) { return key === externalSelectedKeys[index]; })) {
                return prevKeys;
            }
            return __spreadArray([], externalSelectedKeys, true);
        });
        if (Array.isArray(tableProps.dataSource)) {
            var selectedKeySet_1 = new Set(externalSelectedKeys);
            var mappedSelectedRows = tableProps.dataSource.filter(function (record, index) {
                return selectedKeySet_1.has(resolveRowKey(record, index));
            });
            setSelectedRows(mappedSelectedRows);
        }
    }, [
        showSelection,
        userRowSelection === null || userRowSelection === void 0 ? void 0 : userRowSelection.selectedRowKeys,
        tableProps.dataSource,
        resolveRowKey,
    ]);
    // 合并列配置
    var finalColumns = useMemo(function () {
        var baseColumns = resizableColumns ? resizedColumns : visibleColumns;
        var hasActionColumn = baseColumns.some(function (col) {
            return col.key === "action" ||
                col.key === "defaultActions" ||
                (typeof col.title === "string" && col.title.includes("操作"));
        });
        if (defaultActionColumn && !hasActionColumn) {
            return __spreadArray(__spreadArray([], baseColumns, true), [defaultActionColumn], false);
        }
        return baseColumns;
    }, [resizableColumns, resizedColumns, visibleColumns, defaultActionColumn]);
    // 合并滚动配置
    var mergedScroll = useMemo(function () {
        var userScroll = tableProps.scroll;
        if (!autoScroll) {
            return userScroll;
        }
        // 如果用户已经设置了 scroll.y，则使用用户的配置
        if ((userScroll === null || userScroll === void 0 ? void 0 : userScroll.y) !== undefined) {
            return userScroll;
        }
        // 自动计算滚动高度
        // 即使 scrollHeight 为 undefined 或 0，也要设置一个合理的默认值
        // 这样可以确保表格始终有滚动能力
        if (scrollHeight !== undefined && scrollHeight > 0) {
            return __assign(__assign({}, userScroll), { y: scrollHeight, x: (userScroll === null || userScroll === void 0 ? void 0 : userScroll.x) || "max-content" });
        }
        // 如果 scrollHeight 还没计算出来，返回一个临时的滚动配置
        // 使用一个较大的默认值，等待计算完成后再更新
        return __assign(__assign({}, userScroll), { y: 400, x: (userScroll === null || userScroll === void 0 ? void 0 : userScroll.x) || "max-content" });
    }, [autoScroll, scrollHeight, tableProps.scroll]);
    // 构建最终的表格属性
    // 确定 summary 值：如果明确传入 undefined，则完全禁用；否则使用传入的 summary 或 tableProps.summary
    // 优先使用 summaryWithColumns（如果提供），它会接收最终列配置
    var finalSummary = useMemo(function () {
        if (summaryWithColumns) {
            // 如果提供了 summaryWithColumns，使用它并传入 finalColumns
            // 注意：这里返回一个函数，该函数会在每次渲染时使用最新的 finalColumns
            return function (pageData) {
                return summaryWithColumns(pageData, finalColumns);
            };
        }
        if (summary === undefined && tableProps.summary === undefined) {
            return undefined;
        }
        return summary !== undefined ? summary : tableProps.summary;
    }, [summary, summaryWithColumns, tableProps.summary, finalColumns]);
    // 构建基础表格属性，排除 summary（如果为 undefined）
    var mergedTableClassName = [tableProps.className, "comps-table-root"]
        .filter(Boolean)
        .join(" ");
    var baseTableProps = __assign(__assign({}, tableProps), { className: mergedTableClassName, rowSelection: mergedRowSelection, loading: loading, pagination: currentPagination, onChange: handleTableChange, 
        // 使用合并后的列配置
        columns: finalColumns, components: mergedComponents, footer: footer !== undefined ? footer : tableProps.footer, scroll: mergedScroll });
    // 只有当 finalSummary 不是 undefined 时才添加 summary 属性
    var finalTableProps = __assign(__assign({}, baseTableProps), (finalSummary !== undefined ? { summary: finalSummary } : {}));
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: "\n          /* \u53EF\u8C03\u6574\u5217\u5BBD\u6837\u5F0F */\n          .react-resizable {\n            position: relative;\n            background-clip: padding-box;\n          }\n\n          .react-resizable-handle {\n            position: absolute;\n            right: 0;\n            top: 0;\n            bottom: 0;\n            width: 8px;\n            height: 100%;\n            cursor: col-resize;\n            z-index: 1000 !important;\n            background: transparent;\n            pointer-events: auto !important;\n            touch-action: none;\n          }\n\n          /* \u589E\u5F3A\u60AC\u505C\u6548\u679C */\n          .react-resizable-handle:hover {\n            background-color: rgba(24, 144, 255, 0.15);\n          }\n\n          /* \u62D6\u62FD\u65F6\u663E\u793A\u7684\u8F85\u52A9\u7EBF - \u66F4\u52A0\u660E\u663E */\n          .react-resizable-handle:hover::after,\n          .react-resizable-handle-active::after {\n            content: '';\n            position: absolute;\n            top: 0;\n            bottom: 0;\n            right: 3px;\n            width: 2px;\n            background-color: #1890ff;\n            opacity: 1;\n          }\n\n          /* \u5217\u5934\u5355\u5143\u683C\u6837\u5F0F\u8C03\u6574 */\n          th.ant-table-cell {\n            position: relative !important;\n          }\n\n          /* \u786E\u4FDD\u56FA\u5B9A\u5217\u4E5F\u53EF\u4EE5\u8C03\u6574\u5BBD\u5EA6 */\n          th.ant-table-cell-fix-left,\n          th.ant-table-cell-fix-right {\n            position: relative !important;\n          }\n\n          /* \u786E\u4FDD\u56FA\u5B9A\u5217\u4E2D\u7684\u62D6\u52A8\u624B\u67C4\u53EF\u4EE5\u6B63\u5E38\u5DE5\u4F5C */\n          th.ant-table-cell-fix-left .react-resizable-handle,\n          th.ant-table-cell-fix-right .react-resizable-handle {\n            z-index: 1001 !important;\n            pointer-events: auto !important;\n            touch-action: none;\n          }\n\n          /* \u786E\u4FDD\u7B2C\u4E00\u5217\uFF08\u6700\u5DE6\u4FA7\u56FA\u5B9A\u5217\uFF09\u7684\u62D6\u52A8\u624B\u67C4\u53EF\u89C1 */\n          th.ant-table-cell-fix-left:first-child .react-resizable-handle {\n            display: block !important;\n            visibility: visible !important;\n            opacity: 0.3 !important;\n          }\n\n          th.ant-table-cell-fix-left:first-child:hover .react-resizable-handle {\n            opacity: 1 !important;\n          }\n\n          /* \u786E\u4FDD\u62D6\u52A8\u624B\u67C4\u5728\u6240\u6709\u60C5\u51B5\u4E0B\u90FD\u53EF\u89C1\u4E14\u53EF\u4EA4\u4E92 */\n          .ant-table-thead > tr > th .react-resizable-handle {\n            display: block !important;\n            visibility: visible !important;\n            opacity: 0.3;\n            transition: opacity 0.2s;\n            pointer-events: auto !important;\n            touch-action: none;\n          }\n\n          .ant-table-thead > tr > th:hover .react-resizable-handle {\n            opacity: 1;\n          }\n\n          /* \u786E\u4FDD\u62D6\u62FD\u65F6\u624B\u67C4\u53EF\u89C1 */\n          .react-resizable-handle-active {\n            opacity: 1 !important;\n            background-color: rgba(24, 144, 255, 0.2) !important;\n            z-index: 1001 !important;\n          }\n\n          /* \u9632\u6B62\u8868\u683C\u5185\u5BB9\u9009\u62E9\u5E72\u6270\u62D6\u62FD */\n          .ant-table-thead > tr > th {\n            user-select: none;\n          }\n\n          /* \u786E\u4FDD Resizable \u5BB9\u5668\u53EF\u4EE5\u63A5\u6536\u4E8B\u4EF6 */\n          .react-resizable > * {\n            pointer-events: auto;\n          }\n\n          /* CompsTable\uFF1A\u8868\u683C\u533A\u53EF\u6EDA\u52A8\uFF0C\u5206\u9875\u6761\u56FA\u5B9A\u5728\u7EC4\u4EF6\u53EF\u89C6\u533A\u57DF\u5E95\u90E8 */\n          .comps-table-root.ant-table-wrapper {\n            display: flex !important;\n            flex-direction: column;\n            height: 100%;\n            min-height: 0;\n            flex: 1;\n          }\n          .comps-table-root .ant-spin-nested-loading {\n            flex: 1;\n            min-height: 0;\n            display: flex;\n            flex-direction: column;\n          }\n          .comps-table-root .ant-spin-container {\n            flex: 1;\n            min-height: 0;\n            display: flex;\n            flex-direction: column;\n          }\n          .comps-table-root .ant-table {\n            flex: 1;\n            min-height: 0;\n            display: flex;\n            flex-direction: column;\n          }\n          .comps-table-root .ant-table-title {\n            flex-shrink: 0;\n          }\n          .comps-table-root .ant-table-container {\n            flex: 1;\n            min-height: 0;\n          }\n          .comps-table-root .ant-table-footer {\n            flex-shrink: 0;\n          }\n          .comps-table-root .ant-table-pagination {\n            flex-shrink: 0;\n            margin-top: auto;\n            position: sticky;\n            bottom: 0;\n            z-index: 10;\n            background: var(--ant-color-bg-container, #fff);\n            padding-top: 8px;\n            box-shadow: 0 -4px 8px -6px rgba(0, 0, 0, 0.12);\n          }\n        " }), _jsx("div", __assign({ ref: containerRef, className: className, style: __assign({ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", width: "100%" }, style) }, { children: _jsx(Table, __assign({}, finalTableProps)) }))] }));
}
export default CompsTable;
